import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, ReceiptPoundSterling, Send, Truck, PackageCheck, Trash2,
  Search, Clock, FileText, Mail, Hourglass,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MoveToDropdown } from '@/components/kanban/KanbanShared';
import { OrderProfitBadge } from '@/components/kanban/OrderProfitBadge';

import XeroInvoiceBadge from '@/components/xero/XeroInvoiceBadge';
import { formatApiError } from '@/utils/apiError';
import { resendOrderEmail } from '@/components/orders/ResendOrderEmailCard';
import useHomePath from '@/hooks/useHomePath';

// ─────────────────────────────────────────────────────────────────────────────
// Static config
// ─────────────────────────────────────────────────────────────────────────────
const BRAND = {
  probound:       { label: 'ProBound',       colour: '#2F5E96', ink: '#fff' },
  prodecor:       { label: 'ProDecor',       colour: '#D5AB09', ink: '#2B2101' },
  proepoxy:       { label: 'ProEpoxy',       colour: '#7A0B7A', ink: '#fff' },
  material_order: { label: 'Material Order', colour: '#1f4030', ink: '#fff' },
};

// Priority bands inside the "To Invoice" column — order matters (top → bottom).
const PRIORITY_BANDS = [
  { key: 'urgent',  label: 'Urgent',   sub: '1–3 working days', colour: '#b91c1c', bg: 'bg-red-50',    border: 'border-red-200' },
  { key: 'soon',    label: 'Priority', sub: 'Due this week',    colour: '#b45309', bg: 'bg-amber-50',  border: 'border-amber-200' },
  { key: 'no_rush', label: 'Standard', sub: 'Planned ahead',    colour: '#1d4ed8', bg: 'bg-blue-50',   border: 'border-blue-200' },
];

// The four kanban columns, each mapping to one or more backend statuses.
// The "next" field is the status this column advances into when a card is
// dropped on the next column to the right — some columns advance in two
// hops (Invoiced holds both `invoiced` and `paid`), so we resolve the
// specific transition per-card in dropOnColumn().
const COLUMNS = [
  {
    key: 'to_invoice',
    title: 'To Invoice',
    icon: ReceiptPoundSterling,
    statuses: ['submitted'],
    colour: '#F59E0B',  // amber — matches Lead CRM 'Quoted'
  },
  {
    key: 'invoiced',
    title: 'Invoiced — awaiting payment',
    icon: Send,
    statuses: ['invoiced'],
    colour: '#3B82F6',  // blue — matches Lead CRM 'Contacted'
  },
];

const PAYMENT_METHODS = [
  { key: 'go_cardless',   label: 'GoCardless' },
  { key: 'bank_transfer', label: 'Bank Transfer' },
  { key: 'card',          label: 'Card' },
  { key: 'cash',          label: 'Cash' },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

/** The figure the app worked out — an estimate until the office confirms the invoice total. */
const orderAppTotal = (o) => {
  const v = o?.pricing_snapshot?.total_inc_vat ?? o?.calculation?.totalCost ?? o?.total;
  if (v != null) return Number(v) || 0;
  return (Number(o?.subtotal) || 0) - (Number(o?.credit_applied) || 0);
};
/** Inc-VAT estimate for the confirmed-total prefill — ProBound snapshots are already inc. VAT, everything else is excl. VAT. */
const orderAppTotalIncVat = (o) => (o?.pricing_snapshot?.total_inc_vat != null ? Number(o.pricing_snapshot.total_inc_vat) : Math.round(orderAppTotal(o) * 1.2 * 100) / 100);

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
};

// Which kanban column does a given order status belong to?
const columnForStatus = (status) => {
  const s = status === 'pending' ? 'submitted' : (status || 'submitted');
  return COLUMNS.find(c => c.statuses.includes(s));
};

// Backend URL to update this order. All four collections have a unified
// PATCH endpoint at /api/staff/orders/{product}/{id}.
const orderUpdateUrl = (order) => `${API_URL}/api/staff/orders/${order.product}/${order.id}`;

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function StaffOrdersQueue() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useContext(AuthContext);
  const canDelete = ['admin', 'manager'].includes(user?.role);
  const [ordersByColumn, setOrdersByColumn] = useState({
    to_invoice: [], invoiced: [],
  });
  const [deletedCount, setDeletedCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [brandFilter, setBrandFilter] = useState(''); // '' = all · 'probound' | 'proepoxy' | 'prodecor'
  const [dragging, setDragging] = useState(null); // {order, sourceColumn}

  // Modal state for transitions that need extra metadata.
  const [invoiceModal, setInvoiceModal] = useState(null); // {order}
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [confirmedTotal, setConfirmedTotal] = useState('');
  const [creditContext, setCreditContext] = useState(null); // {is_ambassador, credit_balance, max_applicable, ...}
  const [creditAmount, setCreditAmount] = useState('');
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null); // {order}
  const [paymentMethod, setPaymentMethod] = useState('');
  const [dispatchModal, setDispatchModal] = useState(null); // {order}
  const [expectedDate, setExpectedDate] = useState(todayPlus(3));

  // ── data fetching ─────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Invoice Board only cares about pre-payment statuses. Paid/onward
      // live on the Orders Board (/staff/order-processing).
      const combined = 'submitted,invoiced';
      const [rowsRes, delRes, expRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/orders?status=${combined}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/staff/orders/recently-deleted`, { credentials: 'include' }),
        fetch(`${API_URL}/api/staff/orders/expired-invoices`, { credentials: 'include' }),
      ]);
      if (!rowsRes.ok) throw new Error(formatApiError(await rowsRes.json().catch(() => ({}))) || 'Failed to load orders');
      const rows = await rowsRes.json();
      const del = delRes.ok ? await delRes.json() : [];

      const buckets = { to_invoice: [], invoiced: [] };
      rows.forEach(r => {
        const col = columnForStatus(r.status);
        if (col) buckets[col.key].push(r);
      });

      // Sort each column so drag operations feel deterministic.
      // — To Invoice: priority band order, then FIFO by submitted_at asc
      //   (oldest at the top of each band; new cards join the bottom).
      const priorityRank = { urgent: 0, soon: 1, no_rush: 2 };
      buckets.to_invoice.sort((a, b) => {
        const pa = priorityRank[a.priority || 'no_rush'];
        const pb = priorityRank[b.priority || 'no_rush'];
        if (pa !== pb) return pa - pb;
        return (a.submitted_at || a.created_at || '').localeCompare(b.submitted_at || b.created_at || '');
      });
      // — Invoiced: newest invoiced first (surface just-invoiced work).
      buckets.invoiced.sort((a, b) => (b.invoiced_at || '').localeCompare(a.invoiced_at || ''));

      setOrdersByColumn(buckets);
      setDeletedCount(Array.isArray(del) ? del.length : 0);
      setExpiredCount(expRes.ok ? ((await expRes.json()).rows || []).length : 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── search + brand filter (applied to visible cards only) ─────────
  const searchLower = search.trim().toLowerCase();
  // Treat legacy `material_order` product slug as ProBound so it isn't hidden
  // when the user filters to ProBound.
  const brandKeyOf = (o) => (o.product === 'material_order' ? 'probound' : o.product);
  const matchesSearch = useCallback((o) => {
    if (brandFilter && brandKeyOf(o) !== brandFilter) return false;
    if (!searchLower) return true;
    return [o.reference, o.customer_reference, o.installer_name, o.company_name, o.installer_email, o.invoice_number]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(searchLower));
  }, [searchLower, brandFilter]);

  // ── drag helpers ──────────────────────────────────────────────────
  const beginDrag = (order, sourceColumnKey) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs setData; Chrome doesn't mind but doesn't hurt.
    e.dataTransfer.setData('text/plain', order.id);
    setDragging({ order, sourceColumn: sourceColumnKey });
  };
  const endDrag = () => setDragging(null);
  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // Persist a priority change with an optimistic UI update so drag feels
  // instant. Revert on failure.
  const applyPriority = async (order, newPriority) => {
    if ((order.priority || 'no_rush') === newPriority) return;
    setOrdersByColumn(prev => {
      const copy = { ...prev, to_invoice: prev.to_invoice.map(o =>
        o.id === order.id ? { ...o, priority: newPriority } : o) };
      // Re-sort optimistically.
      const rank = { urgent: 0, soon: 1, no_rush: 2 };
      copy.to_invoice.sort((a, b) => {
        const pa = rank[a.priority || 'no_rush'], pb = rank[b.priority || 'no_rush'];
        if (pa !== pb) return pa - pb;
        return (a.submitted_at || '').localeCompare(b.submitted_at || '');
      });
      return copy;
    });
    try {
      const res = await fetch(orderUpdateUrl(order), {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority: newPriority }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Priority update failed');
      const band = PRIORITY_BANDS.find(b => b.key === newPriority);
      toast.success(`${order.reference || 'Order'} → ${band.label}`);
    } catch (e) {
      toast.error(e.message);
      loadAll();
    }
  };

  // Handle a drop on a kanban column (advance status).
  const dropOnColumn = (targetColumnKey) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    const { order, sourceColumn } = dragging;
    setDragging(null);
    if (sourceColumn === targetColumnKey) return; // same column no-op

    // Which specific status transition are we firing?
    const from = order.status === 'pending' ? 'submitted' : order.status;
    // Only forward transition remaining on the Invoice Board is Submitted → Invoiced.
    // Marking an order as Paid graduates it OFF this board onto the Orders Board
    // (/staff/order-processing) — that's done via the "Mark Paid" button on
    // invoiced cards, not by drag-drop, so it never appears in the transition map.
    const transitionMap = {
      'to_invoice__invoiced':  { modal: 'invoice' },
    };
    const rule = transitionMap[`${sourceColumn}__${targetColumnKey}`];
    if (!rule) {
      // No forward path defined — treat as invalid drop.
      toast.error(`Can't move ${order.reference || 'this order'} that way. Submitted orders drag into Invoiced; once paid they move to the Orders Board.`);
      return;
    }
    if (rule.modal === 'invoice') { openInvoiceModal(order); }
  };

  // Handle a drop on a specific priority band (within To Invoice column).
  const dropOnBand = (bandKey) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    const { order, sourceColumn } = dragging;
    setDragging(null);
    if (sourceColumn !== 'to_invoice') return; // only makes sense inside to_invoice
    applyPriority(order, bandKey);
  };

  // ── Modal submit handlers ─────────────────────────────────────────
  // Open the Mark-Invoiced modal — if this is a Material Order, also
  // fetch the ambassador credit context so the modal can show a
  // "Credit Applied" input for dual-role installer/ambassadors.
  const openInvoiceModal = useCallback(async (order) => {
    setInvoiceNumber(order.xero_invoice_number || '');
    const est = order.confirmed_total ?? order.xero_invoice_total ?? orderAppTotalIncVat(order);
    setConfirmedTotal(est ? Number(est).toFixed(2) : '');
    setCreditAmount('');
    setCreditContext(null);
    setInvoiceModal({ order });
    if (order.product !== 'material_order') return;
    try {
      const r = await fetch(
        `${API_URL}/api/staff/orders/material_order/${order.id}/credit-context`,
        { credentials: 'include' },
      );
      if (!r.ok) return; // Silent — credit UI just doesn't render
      const ctx = await r.json();
      setCreditContext(ctx);
    } catch {
      /* silent — modal still works without credit UI */
    }
  }, []);

  const [resendingId, setResendingId] = useState(null);
  const resendEmail = async (order) => {
    if (!window.confirm(`Resend the ${order.reference} order email (with its product lines) to the office?`)) return;
    const toInstaller = window.confirm('Also send a copy to the installer?');
    setResendingId(order.id);
    try {
      const d = await resendOrderEmail(order.product, order.id, { toInstaller });
      toast.success(`Order email re-sent to ${d.sent_to.join(', ')}`);
      setOrdersByColumn((prev) => Object.fromEntries(Object.entries(prev).map(([k, list]) => [k, list.map((o) => (o.id === order.id ? { ...o, email_resent_at: d.email_resent_at } : o))])));
    } catch (e) { toast.error(e.message); } finally { setResendingId(null); }
  };

  const confirmTotal = async (order) => {
    const current = order.confirmed_total ?? order.xero_invoice_total ?? orderAppTotalIncVat(order);
    const raw = window.prompt(`Confirmed invoice total for ${order.reference} (inc. VAT) — check it against Xero first.\nApp estimate: ${fmtGBP(orderAppTotalIncVat(order))} inc. VAT${order.xero_invoice_total != null ? ` · Xero: ${fmtGBP(order.xero_invoice_total)}` : ''}`, current ? Number(current).toFixed(2) : '');
    if (raw === null) return;
    const val = parseFloat(raw);
    if (!(val > 0)) { toast.error('Enter an amount above £0'); return; }
    try {
      const r = await fetch(`${API_URL}/api/staff/orders/${order.product}/${order.id}/confirm-total`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirmed_total: val }) });
      if (!r.ok) throw new Error(formatApiError(await r.json()) || 'Failed');
      toast.success(`Confirmed ${fmtGBP(val)} — the installer has been told the amount to pay`);
      await loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const submitInvoice = async () => {
    if (!invoiceNumber.trim()) { toast.error('Invoice number is required.'); return; }
    const confirmedValue = parseFloat(confirmedTotal);
    if (!(confirmedValue > 0)) { toast.error('Enter the confirmed invoice total — this is the exact amount the installer will be told to pay.'); return; }
    const order = invoiceModal.order;
    const rawCredit = (creditAmount || '').trim();
    const creditValue = rawCredit === '' ? 0 : parseFloat(rawCredit);
    // Validate credit before firing anything — never send half a workflow.
    if (rawCredit !== '' && (Number.isNaN(creditValue) || creditValue < 0)) {
      toast.error('Credit amount must be a positive number.');
      return;
    }
    if (creditValue > 0) {
      const cap = Math.min(
        creditContext?.credit_balance ?? Infinity,
        creditContext?.max_applicable ?? Infinity,
      );
      if (creditValue > cap + 0.005) {
        toast.error(`Credit capped at £${cap.toFixed(2)}.`);
        return;
      }
    }
    try {
      // Apply credit first — if the balance debit fails, we DO NOT want
      // to mark the order invoiced with a stale total. Order marks after.
      if (creditValue > 0 && creditContext?.affiliate_id) {
        setApplyingCredit(true);
        const cr = await fetch(
          `${API_URL}/api/staff/affiliates/${creditContext.affiliate_id}/redeem-credit`,
          {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: creditValue,
              reference_type: 'installer_material_orders',
              reference_id: order.id,
              notes: `Applied at invoicing (Inv ${invoiceNumber.trim()})`,
            }),
          },
        );
        if (!cr.ok) {
          throw new Error(formatApiError(await cr.json().catch(() => ({}))) || 'Credit application failed');
        }
      }
      const res = await fetch(orderUpdateUrl(order), {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'invoiced', invoice_number: invoiceNumber.trim(), confirmed_total: confirmedValue }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to mark invoiced');
      const msg = creditValue > 0
        ? `${order.reference || 'Order'} → Invoiced (£${creditValue.toFixed(2)} credit applied)`
        : `${order.reference || 'Order'} → Invoiced`;
      toast.success(msg);
      setInvoiceModal(null);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplyingCredit(false);
    }
  };

  const submitPayment = async () => {
    if (!paymentMethod) { toast.error('Payment method is required.'); return; }
    const order = paymentModal.order;
    try {
      const res = await fetch(orderUpdateUrl(order), {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'paid', payment_method: paymentMethod }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to mark paid');
      toast.success(`${order.reference || 'Order'} → Paid${paymentMethod === 'cash' ? ' · added to Cash Log' : ''}. Moved to Orders Board.`);
      setPaymentModal(null);
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const submitDispatch = async () => {
    if (!expectedDate) { toast.error('Expected delivery date is required.'); return; }
    const order = dispatchModal.order;
    try {
      const res = await fetch(orderUpdateUrl(order), {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'dispatched', expected_delivery_date: expectedDate }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to mark dispatched');
      toast.success(`${order.reference || 'Order'} → Dispatched`);
      setDispatchModal(null);
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Soft delete + Undo ─────────────────────────────────────────────
  // Fires POST /api/staff/orders/{product}/{id}/delete and shows a 5s Undo
  // toast. Optimistic: the card disappears from the board immediately and
  // only comes back if Undo is tapped (or the request fails).
  const productSlugFor = (order) => order.product;
  const restoreOrder = async (order) => {
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${productSlugFor(order)}/${order.id}/restore`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Restore failed');
      toast.success(`${order.reference || 'Order'} restored`);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const deleteOrder = async (order, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!canDelete) return;
    // Optimistic removal: pull it out of its column now, put back on failure.
    const colKey = columnForStatus(order.status)?.key;
    let snapshot = null;
    if (colKey) {
      setOrdersByColumn(prev => {
        snapshot = prev[colKey];
        return { ...prev, [colKey]: prev[colKey].filter(o => o.id !== order.id) };
      });
    }
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${productSlugFor(order)}/${order.id}/delete`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      setDeletedCount((n) => n + 1);
      toast(`${order.reference || 'Order'} moved to Recently Deleted`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: () => restoreOrder(order),
        },
      });
    } catch (err) {
      toast.error(err.message);
      if (colKey && snapshot) {
        setOrdersByColumn(prev => ({ ...prev, [colKey]: snapshot }));
      }
    }
  };

  // ── Card + column rendering ───────────────────────────────────────
  // Route the card to the right per-brand order detail page. Legacy
  // `material_order` orders live in the ProBound detail collection.
  const detailRouteFor = (order) => {
    if (order.product === 'material_order') return `/material-orders/${order.id}`;
    return `/${order.product}/orders/${order.id}`;
  };

  const renderCard = (order, columnKey) => {
    const brand = BRAND[order.product] || {};
    const wait = daysAgo(order.submitted_at || order.created_at);
    const openOrder = (e) => {
      // Suppress the click that immediately follows a drop (the browser
      // fires a click on the source element after a successful drag).
      if (e.defaultPrevented) return;
      if (dragging) return;
      navigate(detailRouteFor(order));
    };
    return (
      <div
        key={order.id}
        draggable
        onDragStart={beginDrag(order, columnKey)}
        onDragEnd={endDrag}
        onClick={openOrder}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOrder(e); } }}
        title="Click to open · drag to move"
        data-testid={`order-card-${order.id}`}
        className={
          'group bg-white border rounded-lg shadow-sm p-3 cursor-pointer active:cursor-grabbing ' +
          'hover:shadow-md hover:border-slate-400 hover:ring-2 hover:ring-slate-300/60 transition select-none ' +
          'border-l-[5px]'
        }
        style={{ borderLeftColor: brand.colour || '#334155' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ background: brand.colour, color: brand.ink }}
          >{brand.label || order.product}</span>
          <span className="font-mono text-xs text-gray-700 truncate flex-1 min-w-0">{order.reference || order.id?.slice(0, 8)}</span>
          {canDelete && (
            <button
              type="button"
              onClick={(e) => deleteOrder(order, e)}
              onMouseDown={(e) => e.stopPropagation()}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded p-1 -mr-1 -my-1 transition"
              data-testid={`order-delete-${order.id}`}
              title="Delete order (goes to Recently Deleted for 14 days)"
              aria-label={`Delete order ${order.reference || ''}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="font-semibold text-sm text-gray-900 truncate">{order.customer_reference || order.installer_name || 'No reference'}</div>
        {order.company_name && (
          <div className="text-[11px] text-gray-500 truncate">{order.company_name}</div>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <span className="font-bold text-sm">{fmtGBP(order.confirmed_total ?? order.total)}{order.confirmed_total == null && <span className="ml-1 text-[9px] font-bold uppercase text-amber-700">est.</span>}</span>
          {wait !== null && (
            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
              <Clock className="w-3 h-3" /> {wait}d
            </span>
          )}
        </div>
        <OrderProfitBadge profit={order.profit} orderId={order.id} />
        {(order.credit_applied || 0) > 0 && (
          <div
            className="mt-1 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-block"
            data-testid={`order-credit-pill-${order.id}`}
            title="Ambassador credit applied to this order"
          >
            {fmtGBP(order.credit_applied)} credit
          </div>
        )}
        {order.payment_method && (
          <div className="mt-1 text-[10px] font-semibold text-emerald-700 truncate" data-testid={`order-paid-method-${order.id}`}>Paid · {PAYMENT_METHODS.find((m) => m.key === order.payment_method)?.label || order.payment_method}{order.payment_method === 'cash' ? ' · in Cash Log' : ''}</div>
        )}
        {order.invoice_number && (
          <div className="mt-1 text-[10px] font-mono text-indigo-700 truncate">Inv: {order.invoice_number}</div>
        )}
        {/* Xero DRAFT push status — only render on Invoiced+ cards where a
            push is expected. Green pill w/ deep-link if pushed, red retry
            pill on error, transient amber "Xero…" while auto-push runs. */}
        {(columnKey === 'invoiced' || order.xero_invoice_id || order.xero_last_error) && (
          <div className="mt-1.5">
            <XeroInvoiceBadge order={order} source={order.product} onRetried={loadAll} />
          </div>
        )}
        {/* Move-to picker on Submitted cards — matches Lead CRM & other boards */}
        {columnKey === 'to_invoice' && (
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <MoveToDropdown
              current="to_invoice"
              options={[
                { key: 'to_invoice', label: 'To Invoice' },
                { key: 'invoiced',   label: 'Invoiced — awaiting payment' },
              ]}
              onChange={() => { openInvoiceModal(order); }}
              testId={`order-move-${order.id}`}
            />
          </div>
        )}
        {/* Raise a Xero invoice from this order — opens the Invoice Centre pre-filled with its lines */}
        {['to_invoice', 'invoiced'].includes(columnKey) && (
          <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/staff/invoices?from=${order.product}&order=${order.id}`); }} draggable={false} onDragStart={(e) => e.preventDefault()} onMouseDown={(e) => e.stopPropagation()}
            className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-800 hover:bg-indigo-100" data-testid={`order-raise-invoice-${order.id}`}>
            <FileText className="w-3.5 h-3.5" /> {order.xero_invoice_number ? `Invoice ${order.xero_invoice_number} · raise another` : 'Raise invoice in Xero'}
          </button>
        )}
        <button type="button" onClick={(e) => { e.stopPropagation(); resendEmail(order); }} disabled={resendingId === order.id} draggable={false} onDragStart={(e) => e.preventDefault()} onMouseDown={(e) => e.stopPropagation()}
          className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60" title={order.email_resent_at ? `Last re-sent ${new Date(order.email_resent_at).toLocaleString('en-GB')}` : 'Re-send the new-order email with its product lines'} data-testid={`order-resend-email-${order.id}`}>
          <Mail className="w-3.5 h-3.5" /> {resendingId === order.id ? 'Sending…' : 'Resend order email'}
        </button>
        {/* Confirmed-total badge — installers can't Mark as Paid until this is set */}
        {columnKey === 'invoiced' && (
          <div className="mt-2 flex items-center justify-between gap-2" onMouseDown={(e) => e.stopPropagation()}>
            {order.confirmed_total != null ? (
              <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`order-confirmed-total-${order.id}`}>Confirmed {fmtGBP(order.confirmed_total)}</span>
            ) : (
              <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`order-total-unconfirmed-${order.id}`}>Amount not confirmed — installer can't pay yet</span>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); confirmTotal(order); }} draggable={false} onDragStart={(e) => e.preventDefault()} className="text-[11px] font-semibold text-blue-700 underline shrink-0" data-testid={`order-confirm-total-${order.id}`}>{order.confirmed_total != null ? 'Correct total' : 'Confirm total'}</button>
          </div>
        )}
        {/* Mark-Paid button on Invoiced cards — graduates the order to the Orders Board */}
        {columnKey === 'invoiced' && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPaymentMethod('');
              setPaymentModal({ order });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            data-testid={`order-mark-paid-${order.id}`}
            className="mt-2 w-full text-[11px] font-semibold px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white transition"
            title="Mark this invoice as paid — moves the order to the Orders Board"
          >
            Mark Paid → Orders Board
          </button>
        )}
      </div>
    );
  };

  const renderToInvoiceColumn = () => {
    const rows = ordersByColumn.to_invoice.filter(matchesSearch);
    const byBand = { urgent: [], soon: [], no_rush: [] };
    rows.forEach(r => { byBand[r.priority || 'no_rush'].push(r); });
    return (
      <div className="flex flex-col gap-3 min-w-0" data-testid="kanban-column-to_invoice">
        {PRIORITY_BANDS.map(band => (
          <div
            key={band.key}
            onDragOver={allowDrop}
            onDrop={dropOnBand(band.key)}
            data-testid={`priority-band-${band.key}`}
            className={`${band.bg} ${band.border} border-2 border-dashed rounded-lg p-2 min-h-[80px] transition ${dragging?.sourceColumn === 'to_invoice' ? 'ring-2 ring-offset-2' : ''}`}
            style={{ '--tw-ring-color': band.colour }}
          >
            <div className="flex items-center justify-between mb-1.5 px-1">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: band.colour }}>{band.label}</div>
                <div className="text-[10px] text-gray-500">{band.sub}</div>
              </div>
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
                style={{ background: band.colour }}
                data-testid={`priority-band-count-${band.key}`}
              >
                {byBand[band.key].length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {byBand[band.key].length === 0 ? (
                <div className="text-[11px] text-gray-400 italic text-center py-2">
                  Drop here for {band.label.toLowerCase()}
                </div>
              ) : (
                byBand[band.key].map(o => renderCard(o, 'to_invoice'))
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPlainColumn = (colKey) => {
    const rows = ordersByColumn[colKey].filter(matchesSearch);
    return (
      <div
        onDragOver={allowDrop}
        onDrop={dropOnColumn(colKey)}
        data-testid={`kanban-column-${colKey}`}
        className={`flex flex-col gap-2 rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 p-2 min-h-[140px] transition ${dragging && dragging.sourceColumn !== colKey ? 'ring-2 ring-slate-400' : ''}`}
      >
        {rows.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic text-center py-6">Nothing here yet</div>
        ) : rows.map(o => renderCard(o, colKey))}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────
  const totalVisible = useMemo(() =>
    Object.values(ordersByColumn).reduce((n, arr) => n + arr.filter(matchesSearch).length, 0),
  [ordersByColumn, matchesSearch]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="text-white shrink-0" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/staff')} data-testid="staff-orders-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <ReceiptPoundSterling className="w-5 h-5 shrink-0" /> Invoice Board
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              Use "Move to" on any card to advance
            </p>
          </div>
          <div className="relative hidden sm:block">
            <Search className="w-4 h-4 text-white/60 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ref, customer, invoice…"
              className="pl-8 h-9 w-72 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              data-testid="kanban-search"
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/staff/orders/expired-invoices')}
            className="text-[11px] text-white/80 hover:text-white shrink-0 hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition"
            data-testid="kanban-expired-count"
            title="Invoices unpaid for 7+ days leave this board and sit in Expired Invoices"
          >
            <Hourglass className="w-3.5 h-3.5" /> {expiredCount} expired
          </button>
          {deletedCount > 0 && (
            <button
              type="button"
              onClick={() => navigate('/staff/orders/recently-deleted')}
              className="text-[11px] text-white/80 hover:text-white shrink-0 hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition"
              data-testid="kanban-deleted-count"
              title={`${deletedCount} orders in the 14-day recycle bin — click to review`}
            >
              <Trash2 className="w-3.5 h-3.5" /> {deletedCount} deleted
            </button>
          )}
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0" aria-label="Home">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
        {/* Brand filter pills — sit under the header so they're visible on mobile too */}
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 pb-3 pill-row items-center">
          <span className="text-[11px] uppercase tracking-wider text-white/50 mr-1 shrink-0">Brand</span>
          <button
            onClick={() => setBrandFilter('')}
            data-testid="kanban-brand-all"
            className={`shrink-0 text-xs px-3 py-1 rounded-full border transition ${brandFilter === '' ? 'bg-white text-slate-900 border-white font-semibold' : 'bg-white/10 text-white/80 border-white/20 hover:bg-white/20'}`}
          >
            All
          </button>
          {['probound', 'proepoxy', 'prodecor'].map((b) => {
            const conf = BRAND[b];
            const active = brandFilter === b;
            return (
              <button
                key={b}
                onClick={() => setBrandFilter(active ? '' : b)}
                data-testid={`kanban-brand-${b}`}
                className={`shrink-0 text-xs px-3 py-1 rounded-full border transition font-semibold ${active ? '' : 'bg-white/10 text-white/80 border-white/20 hover:bg-white/20'}`}
                style={active ? { background: conf.colour, color: conf.ink, borderColor: conf.colour } : {}}
              >
                {conf.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 max-w-[1600px] w-full mx-auto px-3 sm:px-4 py-4 min-h-0 overflow-hidden">
        {loading ? (
          <div className="text-center text-gray-500 italic py-16">Loading orders…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 h-full min-h-0">
            {COLUMNS.map(col => (
              <div key={col.key} className="flex flex-col min-h-0">
                <div className="rounded-t-lg text-white px-3 py-2 flex items-center gap-2" style={{ backgroundColor: col.colour }}>
                  <col.icon className="w-4 h-4 shrink-0" />
                  <span className="font-bold text-sm flex-1 truncate">{col.title}</span>
                  <span className="text-[10px] bg-white/20 rounded-full px-2 py-0.5" data-testid={`kanban-count-${col.key}`}>
                    {ordersByColumn[col.key].filter(matchesSearch).length}
                  </span>
                </div>
                {col.key === 'invoiced' && (
                  <div className="text-[10px] text-gray-600 bg-blue-50 border-x border-b border-blue-100 px-3 py-1 flex items-center justify-between gap-2" data-testid="kanban-invoiced-expiry-note">
                    <span>Unpaid after 7 days → moves to Expired Invoices</span>
                    <button type="button" onClick={() => navigate('/staff/orders/expired-invoices')} className="font-bold text-blue-700 underline shrink-0" data-testid="kanban-expired-link">{expiredCount} expired</button>
                  </div>
                )}
                <div className="flex-1 min-h-0 overflow-y-auto pt-2 pb-4 pr-1">
                  {col.key === 'to_invoice' ? renderToInvoiceColumn() : renderPlainColumn(col.key)}
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && totalVisible === 0 && (searchLower || brandFilter) && (
          <p className="text-center text-gray-500 italic mt-4" data-testid="kanban-empty-filtered">
            No orders match the current filters{brandFilter ? ` (${BRAND[brandFilter].label}${searchLower ? ` · "${search}"` : ''})` : ` — "${search}"`}.
          </p>
        )}
      </div>

      {/* ── Invoice number modal ─────────────────────────── */}
      <Dialog open={!!invoiceModal} onOpenChange={(o) => !o && setInvoiceModal(null)}>
        <DialogContent data-testid="modal-invoice" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Invoiced</DialogTitle>
            <DialogDescription>Enter the Xero invoice number and the confirmed total for {invoiceModal?.order?.reference}. The installer can only pay once you've confirmed the amount.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label htmlFor="inv" className="text-sm">Invoice number</Label>
              <Input id="inv" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2601" className="mt-1" autoFocus data-testid="modal-invoice-number" />
            </div>
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3">
              <Label htmlFor="conf-total" className="text-sm font-bold text-amber-900">Confirmed invoice total (inc. VAT)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                <Input id="conf-total" type="number" step="0.01" min="0" value={confirmedTotal} onChange={(e) => setConfirmedTotal(e.target.value)} className="pl-7 font-bold text-lg h-11" data-testid="modal-invoice-confirmed-total" />
              </div>
              <p className="text-xs text-amber-900 mt-1.5">
                App estimate: <strong>{fmtGBP(orderAppTotalIncVat(invoiceModal?.order || {}))}</strong> inc. VAT{invoiceModal?.order?.pricing_snapshot?.total_inc_vat == null && <> ({fmtGBP(orderAppTotal(invoiceModal?.order || {}))} excl. VAT + 20%)</>}
                {invoiceModal?.order?.xero_invoice_total != null && <> · Xero invoice: <strong>{fmtGBP(invoiceModal.order.xero_invoice_total)}</strong></>}
                . Check against Xero before confirming — this is the exact amount the installer is told to pay.
              </p>
            </div>

            {/* Ambassador credit — only when this installer is a dual-role
                ambassador with a positive balance AND the order still has
                remaining balance to credit against. */}
            {creditContext?.is_ambassador
              && (creditContext.credit_balance || 0) > 0
              && (creditContext.max_applicable || 0) > 0 && (
              <div
                className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2"
                data-testid="credit-panel"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                    Ambassador Credit
                  </div>
                  <div className="text-xs text-emerald-800" data-testid="credit-balance">
                    Balance: {fmtGBP(creditContext.credit_balance)}
                  </div>
                </div>
                <div className="text-[11px] text-emerald-800">
                  {creditContext.affiliate_name} · order subtotal {fmtGBP(creditContext.order_subtotal)}
                  {creditContext.already_applied > 0 && ` · already applied ${fmtGBP(creditContext.already_applied)}`}
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label htmlFor="credit" className="text-xs text-emerald-900">Credit to apply (£)</Label>
                    <Input
                      id="credit"
                      type="number"
                      min="0"
                      step="0.01"
                      max={Math.min(creditContext.credit_balance, creditContext.max_applicable)}
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                      data-testid="modal-credit-amount"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const cap = Math.min(creditContext.credit_balance, creditContext.max_applicable);
                      setCreditAmount(cap.toFixed(2));
                    }}
                    className="mb-[2px] px-2 py-1.5 text-[11px] font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white transition"
                    data-testid="credit-apply-max"
                  >
                    Apply max ({fmtGBP(Math.min(creditContext.credit_balance, creditContext.max_applicable))})
                  </button>
                </div>
                <div className="text-[10px] text-emerald-700 italic">
                  Deducts from the ambassador's balance and appears as a negative line on the Xero draft.
                </div>
              </div>
            )}

            {creditContext?.already_applied > 0 && !((creditContext.max_applicable || 0) > 0) && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="credit-exhausted">
                Ambassador credit of {fmtGBP(creditContext.already_applied)} already applied — no remaining order balance to credit against.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceModal(null)} data-testid="modal-invoice-cancel">Cancel</Button>
            <Button
              onClick={submitInvoice}
              disabled={applyingCredit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="modal-invoice-submit"
            >
              {applyingCredit ? 'Applying credit…' : 'Mark Invoiced'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Payment method modal ─────────────────────────── */}
      <Dialog open={!!paymentModal} onOpenChange={(o) => !o && setPaymentModal(null)}>
        <DialogContent data-testid="modal-payment" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>How was {paymentModal?.order?.reference} paid?</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            {PAYMENT_METHODS.map(m => (
              <label key={m.key} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
                <input type="radio" checked={paymentMethod === m.key} onChange={() => setPaymentMethod(m.key)} data-testid={`modal-payment-${m.key}`} />
                <span className="text-sm">{m.label}</span>
                {m.key === 'cash' && <span className="ml-auto text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">goes to Cash Log</span>}
              </label>
            ))}
          </div>
          {paymentMethod === 'cash' && <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="modal-payment-cash-note">{fmtGBP(paymentModal?.order?.confirmed_total ?? paymentModal?.order?.total)} cash from {paymentModal?.order?.customer_reference || paymentModal?.order?.installer_name || 'this customer'} will be added to the Cash Log automatically, dated today.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModal(null)} data-testid="modal-payment-cancel">Cancel</Button>
            <Button onClick={submitPayment} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="modal-payment-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dispatch date modal ──────────────────────────── */}
      <Dialog open={!!dispatchModal} onOpenChange={(o) => !o && setDispatchModal(null)}>
        <DialogContent data-testid="modal-dispatch" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Dispatched</DialogTitle>
            <DialogDescription>When will {dispatchModal?.order?.reference} arrive?</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="edd" className="text-sm">Expected delivery date</Label>
            <Input id="edd" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className="mt-1" data-testid="modal-dispatch-date" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchModal(null)} data-testid="modal-dispatch-cancel">Cancel</Button>
            <Button onClick={submitDispatch} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="modal-dispatch-submit">Mark Dispatched</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
